import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  alpha,
  styled,
  useTheme,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Pagination,
} from "@mui/material";
import { Delete, Edit, Save, Close, Refresh, Add } from "@mui/icons-material";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import { usePageTitle } from "contexts/PageTitleContext";
import { getAllNavigationItems } from "layouts/layout-parts/navigation";

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.06),
    color: theme.palette.text.primary,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.common.white, 0.02)
      : alpha(theme.palette.common.black, 0.02),
  },
  "&:hover": {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04),
    transition: 'background-color 0.2s ease-in-out',
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Permission = () => {
  const theme = useTheme();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  // State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [userTypeDialogOpen, setUserTypeDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [languageFilter, setLanguageFilter] = useState("");
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [navigationItems] = useState(() => getAllNavigationItems());
  const [navigationPermissions, setNavigationPermissions] = useState({});
  const [existingPermissions, setExistingPermissions] = useState([]); // Store existing permissions with IDs
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedPermissionToAdd, setSelectedPermissionToAdd] = useState("");
  const [userTypeFormData, setUserTypeFormData] = useState({
    name: "",
    display_name: "",
    status: "Active",
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  // Cache for user types to avoid multiple API calls
  const userTypesCache = useRef(new Map());
  const fetchingUserTypes = useRef(false);

  // Fetch questions with pagination
  const fetchQuestions = useCallback(async (language = "", pageParam = 1, pageSizeParam = 10) => {
    if (fetchingUserTypes.current) return;

    const cacheKey = `${language || "default"}_${pageParam}_${pageSizeParam}`;
    if (userTypesCache.current.has(cacheKey)) {
      const cachedData = userTypesCache.current.get(cacheKey);
      setQuestions(cachedData.questionList);
      setAvailableLanguages(cachedData.languages);
      if (cachedData.pagination) setPagination(cachedData.pagination);
      setLoading(false);
      return;
    }

    fetchingUserTypes.current = true;
    setLoading(true);
    
    try {
      let url = API_ENDPOINTS.Get_user_types;
      const params = new URLSearchParams();
      params.set("page", String(pageParam));
      params.set("page_size", String(pageSizeParam));
      if (language) params.set("language", language);
      url += (url.includes("?") ? "&" : "?") + params.toString();
      
      const response = await apiService.get(url);
      
      if (response?.success && response?.data) {
        const questionList = Array.isArray(response.data)
          ? response.data
          : response.data.GPTSampleQuestions || response.data.questions || response.data.GPTSampleQuestion || [];
        
        const languages = [...new Set(questionList.map(q => q.language).filter(Boolean))].sort();
        
        const paginationData = response?.pagination || response?.data?.pagination || {};
        if (Object.keys(paginationData).length > 0) {
          setPagination({
            current_page: paginationData.current_page ?? pageParam,
            page_size: paginationData.page_size ?? pageSizeParam,
            total_items: paginationData.total_items ?? 0,
            total_pages: paginationData.total_pages ?? 1,
            has_next: paginationData.has_next ?? false,
            has_previous: paginationData.has_previous ?? false,
            next_page_number: paginationData.next_page_number ?? null,
            previous_page_number: paginationData.previous_page_number ?? null,
          });
          setCurrentPage(paginationData.current_page ?? pageParam);
          setPageSize(paginationData.page_size ?? pageSizeParam);
        } else {
          setPagination(prev => ({ ...prev, total_items: questionList.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
        }
        
        userTypesCache.current.set(cacheKey, {
          questionList,
          languages,
          pagination: response?.pagination || response?.data?.pagination || null,
        });
        
        setQuestions(questionList);
        setAvailableLanguages(languages);
      } else {
        setQuestions([]);
        setAvailableLanguages([]);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.showError("Failed to load questions");
      setQuestions([]);
      setAvailableLanguages([]);
    } finally {
      setLoading(false);
      fetchingUserTypes.current = false;
    }
  }, [toast]);

  const handlePermissionPageChange = (_, value) => {
    setCurrentPage(value);
    fetchQuestions(languageFilter, value, pageSize);
  };

  const handlePermissionPageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    userTypesCache.current.clear();
    fetchQuestions(languageFilter, 1, newSize);
  };

  useEffect(() => {
    setPageTitle("User Types & Permissions");
  }, [setPageTitle]);

  // Fetch questions when language filter changes
  useEffect(() => {
    setCurrentPage(1);
    userTypesCache.current.clear();
    fetchQuestions(languageFilter, 1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageFilter]);

  // Cache for permissions to avoid multiple API calls
  const permissionsCache = useRef(new Map());
  const fetchingPermissions = useRef(new Set());

  // Fetch existing permissions for a user type
  const fetchPermissions = useCallback(async (userTypeId) => {
    // Prevent multiple simultaneous calls for the same userTypeId
    if (fetchingPermissions.current.has(userTypeId)) {
      return;
    }

    // Check cache first
    if (permissionsCache.current.has(userTypeId)) {
      const cachedData = permissionsCache.current.get(userTypeId);
      setExistingPermissions(cachedData.permissionsList);
      setNavigationPermissions(cachedData.navigationPermissions);
      setLoadingPermissions(false);
      return;
    }

    fetchingPermissions.current.add(userTypeId);
    setLoadingPermissions(true);
    
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.Get_permissions}?user_type_id=${userTypeId}`
      );
      
      if (response?.success && response?.data) {
        // New response format: { success: true, data: { "dashboard": true, "analytics": false } }
        const permissionsObj = response.data;
        
        // Convert to array format for existingPermissions state
        const permissionsList = Object.keys(permissionsObj).map(key => ({
          tab_name: key,
          permission: key,
          allowed: permissionsObj[key] === true,
          id: key, // Use key as identifier
        }));
        
        // Map existing permissions to navigation permissions state
        // true = Yes checked, false = No checked, undefined = No checked (default)
        const initialPermissions = {};
        navigationItems.forEach(nav => {
          // Check if permission exists in response data
          const permissionValue = permissionsObj[nav.permission];
          if (permissionValue !== undefined) {
            // Permission exists in response - set based on value
            initialPermissions[nav.permission] = permissionValue === true;
          } else {
            // Permission not in response - default to false (No checked)
            initialPermissions[nav.permission] = false;
          }
        });
        
        // Cache the result
        permissionsCache.current.set(userTypeId, {
          permissionsList,
          navigationPermissions: initialPermissions
        });
        
        setExistingPermissions(permissionsList);
        setNavigationPermissions(initialPermissions);
      } else {
        // No existing permissions, initialize all to false (No checked)
        const initialPermissions = {};
        navigationItems.forEach(nav => {
          initialPermissions[nav.permission] = false;
        });
        
        // Cache empty result
        permissionsCache.current.set(userTypeId, {
          permissionsList: [],
          navigationPermissions: initialPermissions
        });
        
        setNavigationPermissions(initialPermissions);
        setExistingPermissions([]);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      // Initialize all to false on error (No checked)
      const initialPermissions = {};
      navigationItems.forEach(nav => {
        initialPermissions[nav.permission] = false;
      });
      setNavigationPermissions(initialPermissions);
      setExistingPermissions([]);
    } finally {
      setLoadingPermissions(false);
      fetchingPermissions.current.delete(userTypeId);
    }
  }, [navigationItems]);

  // Open permission dialog
  const handleOpenPermissionDialog = async (item) => {
    if (item) {
      const userTypeId = item.id || item.question_id || item.user_type_id;
      setEditingId(userTypeId);
      setPermissionDialogOpen(true);
      // Fetch existing permissions (will use cache if available)
      await fetchPermissions(userTypeId);
    }
  };

  // Close permission dialog
  const handleClosePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setEditingId(null);
    setNavigationPermissions({});
    setExistingPermissions([]);
    setSelectedPermissionToAdd("");
  };

  // Open user type dialog for add/edit
  const handleOpenUserTypeDialog = (item = null) => {
    if (item) {
      setEditingId(item.id || item.user_type_id);
      setUserTypeFormData({
        name: item.name || "",
        display_name: item.display_name || "",
        status: item.status || "Active",
      });
    } else {
      setEditingId(null);
      setUserTypeFormData({
        name: "",
        display_name: "",
        status: "Active",
      });
    }
    setUserTypeDialogOpen(true);
  };

  // Close user type dialog
  const handleCloseUserTypeDialog = () => {
    setUserTypeDialogOpen(false);
    setEditingId(null);
    setUserTypeFormData({
      name: "",
      display_name: "",
      status: "Active",
    });
  };

  // Handle navigation permission checkbox change (mutually exclusive Yes/No)
  const handleNavigationPermissionChange = (permission, value) => {
    setNavigationPermissions(prev => ({
      ...prev,
      [permission]: value // true for Yes, false for No
    }));
  };

  // Save user type (add or update)
  const handleSaveUserType = async () => {
    if (!userTypeFormData.name?.trim() || !userTypeFormData.display_name?.trim()) {
      toast.showError("Please fill all required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: userTypeFormData.name.trim(),
        display_name: userTypeFormData.display_name.trim(),
        status: userTypeFormData.status,
      };

      let response;
      if (editingId) {
        // Update
        payload.user_type_id = editingId;
        response = await apiService.put(API_ENDPOINTS.Update_user_type, payload);
      } else {
        // Add
        response = await apiService.post(API_ENDPOINTS.Add_user_type, payload);
      }

      if (response?.success) {
        toast.showSuccess(editingId ? "User type updated successfully" : "User type added successfully");
        handleCloseUserTypeDialog();
        // Clear cache and refetch
        userTypesCache.current.clear();
        fetchQuestions(languageFilter, currentPage, pageSize);
      } else {
        toast.showError(response?.message || "Failed to save user type");
      }
    } catch (error) {
      console.error("Error saving user type:", error);
      toast.showError(error?.message || "Failed to save user type");
    } finally {
      setSaving(false);
    }
  };

  // Add a single permission
  const handleAddSinglePermission = async () => {
    if (!selectedPermissionToAdd || !editingId) {
      toast.showError("Please select a permission to add");
      return;
    }

    const navItem = navigationItems.find(nav => nav.permission === selectedPermissionToAdd);
    if (!navItem) {
      toast.showError("Invalid permission selected");
      return;
    }

    // Check if permission already exists (based on the get response format)
    const existingPerm = existingPermissions.find(
      p => (p.tab_name === selectedPermissionToAdd || p.tab_name === navItem.name?.toLowerCase()) ||
           (p.permission === selectedPermissionToAdd || p.permission === navItem.name?.toLowerCase())
    );

    if (existingPerm) {
      toast.showError("This permission already exists");
      return;
    }
    
    // Also check if it's already in navigationPermissions with a value
    if (navigationPermissions[selectedPermissionToAdd] !== undefined) {
      toast.showError("This permission is already configured");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_type_id: editingId,
        permissions: {
          [selectedPermissionToAdd]: true, // Default to true when adding
        },
      };

      const response = await apiService.post(API_ENDPOINTS.Add_permission, payload);
      
      if (response?.success) {
        toast.showSuccess("Permission added successfully");
        setSelectedPermissionToAdd("");
        // Clear cache and refresh permissions
        if (editingId) {
          permissionsCache.current.delete(editingId);
          await fetchPermissions(editingId);
        }
        // Clear user types cache and refetch
        userTypesCache.current.clear();
        fetchQuestions(languageFilter, currentPage, pageSize);
      } else {
        toast.showError(response?.message || "Failed to add permission");
      }
    } catch (error) {
      console.error("Error adding permission:", error);
      toast.showError(error?.message || "Failed to add permission");
    } finally {
      setSaving(false);
    }
  };


  // Delete individual permission
  const handleDeletePermission = async (permissionId) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Permission",
      message: "Are you sure you want to delete this permission?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.Delete_permission}?permission_id=${permissionId}`
      );
      
      if (response?.success) {
        toast.showSuccess("Permission deleted successfully");
        // Clear cache and refresh permissions
        if (editingId) {
          permissionsCache.current.delete(editingId);
          await fetchPermissions(editingId);
        }
        // Clear user types cache and refetch
        userTypesCache.current.clear();
        fetchQuestions(languageFilter, currentPage, pageSize);
      } else {
        toast.showError(response?.message || "Failed to delete permission");
      }
    } catch (error) {
      console.error("Error deleting permission:", error);
      toast.showError(error?.message || "Failed to delete permission");
    }
  };

  // Save permissions (Add or Update)
  const handleSavePermissions = async () => {
    if (!editingId) {
      toast.showError("User type ID is required");
      return;
    }
  
    setSaving(true);
    try {
      // Build full permissions object (true / false)
      const permissionsObj = {};
      navigationItems.forEach(nav => {
        permissionsObj[nav.permission] =
          navigationPermissions[nav.permission] === true;
      });
  
      const payload = {
        user_type_id: editingId,
        permissions: permissionsObj,
      };
  
      // 🔥 ALWAYS CALL ADD API (for both add & update)
      const response = await apiService.post(
        API_ENDPOINTS.Add_permission,
        payload
      );
  
      if (response?.success) {
        toast.showSuccess("Permissions saved successfully");
        // Clear cache for this user type
        if (editingId) {
          permissionsCache.current.delete(editingId);
        }
        // Clear user types cache and refetch
        userTypesCache.current.clear();
        handleClosePermissionDialog();
        fetchQuestions(languageFilter, currentPage, pageSize);
      } else {
        const errorMsg =
          response?.message || response?.error || "Failed to save permissions";
        toast.showError(errorMsg);
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
  
      let errorMessage = "Failed to save permissions";
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData)) {
          errorMessage = errorData.map(e => e.message || "Error").join(", ");
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
  
      toast.showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  

  // Delete user type
  const handleDeleteUserType = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete User Type",
      message: "Are you sure you want to delete this user type?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.Delete_user_type}?user_type_id=${id}`
      );
      
      if (response?.success) {
        toast.showSuccess("User type deleted successfully");
        // Clear cache and refetch
        userTypesCache.current.clear();
        fetchQuestions(languageFilter, currentPage, pageSize);
      } else {
        toast.showError(response?.message || "Failed to delete user type");
      }
    } catch (error) {
      console.error("Error deleting user type:", error);
      toast.showError(error?.message || "Failed to delete user type");
    } finally {
      setDeletingId(null);
    }
  };

  // Filter questions by language
  const filteredQuestions = languageFilter
    ? questions.filter(q => q.language === languageFilter)
    : questions;

  return (
    <PageContainer>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            User Types & Permissions
          </Typography>
          <Muted variant="body2">Manage user types and their permissions</Muted>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={pageSize} label="Rows" onChange={handlePermissionPageSizeChange}>
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => handleOpenUserTypeDialog()}
          >
            Add User Type
          </Button>
          <Button 
            startIcon={<Refresh />} 
            variant="outlined" 
            onClick={() => {
              userTypesCache.current.clear();
              fetchQuestions(languageFilter, currentPage, pageSize);
            }} 
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </HeaderCard>


      {/* Questions Table */}
      <TableCard>
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2, 
            overflow: "hidden",
            boxShadow: (theme) => theme.palette.mode === 'dark' 
              ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
              : '0 2px 8px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell className="header" sx={{ width: '80px', textAlign: 'center' }}>
                  Sr No
                </StyledTableCell>
                <StyledTableCell className="header">Name</StyledTableCell>
                <StyledTableCell className="header">Display Name</StyledTableCell>
                <StyledTableCell className="header">Permissions</StyledTableCell>
                <StyledTableCell className="header" sx={{ width: '250px', textAlign: 'center' }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredQuestions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No user types found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredQuestions.map((item, index) => (
                  <StyledTableRow key={item.id || item.question_id}>
                    <StyledTableCell sx={{ textAlign: 'center' }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        {index + 1}
                      </Typography>
                    </StyledTableCell>
                    {/* <StyledTableCell>
                      <Chip 
                        label={item.language || "N/A"} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </StyledTableCell> */}
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ maxWidth: 400 }}>
                        {item.name || "N/A"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ maxWidth: 400 }}>
                        {item.display_name || "N/A"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                    <Tooltip title="Give Permissions">
                          <Button 
                            startIcon={<Edit/>}
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenPermissionDialog(item)}
                            sx={{ backgroundColor: "#238d61" }}
                          >
                            Permissions
                          </Button>
                        </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                        <Tooltip title="Edit User Type">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenUserTypeDialog(item)}
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                       
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteUserType(item.id || item.user_type_id)}
                              disabled={deletingId === (item.id || item.user_type_id)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                }
                              }}
                            >
                              {deletingId === (item.id || item.user_type_id) ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {pagination.total_items > 0
            ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items}`
            : (questions.length > 0 ? `Showing ${questions.length} items` : 'No items')}
        </Typography>
        {pagination.total_pages > 1 && (
          <Pagination count={pagination.total_pages} page={currentPage} onChange={handlePermissionPageChange} color="primary" />
        )}
      </Box>

      {/* User Type Add/Edit Dialog */}
      <Dialog open={userTypeDialogOpen} onClose={handleCloseUserTypeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingId ? "Edit User Type" : "Add User Type"}
            </Typography>
            <IconButton onClick={handleCloseUserTypeDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <TextField
              label="Name *"
              value={userTypeFormData.name}
              onChange={(e) => setUserTypeFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              placeholder="Enter user type name..."
            />
            <TextField
              label="Display Name *"
              value={userTypeFormData.display_name}
              onChange={(e) => setUserTypeFormData(prev => ({ ...prev, display_name: e.target.value }))}
              fullWidth
              placeholder="Enter display name..."
            />
            {/* <FormControl fullWidth>
              <InputLabel>Status *</InputLabel>
              <Select
                value={userTypeFormData.status}
                onChange={(e) => setUserTypeFormData(prev => ({ ...prev, status: e.target.value }))}
                label="Status *"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserTypeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveUserType}
            variant="contained"
            disabled={saving || !userTypeFormData.name?.trim() || !userTypeFormData.display_name?.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            {saving ? "Saving..." : editingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog open={permissionDialogOpen} onClose={handleClosePermissionDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
             Give Permission
            </Typography>
            <IconButton onClick={handleClosePermissionDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            {loadingPermissions ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Add Permission Section */}
                {/* <Box sx={{ 
                  p: 2, 
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, 
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                    Add New Permission
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Select Permission</InputLabel>
                      <Select
                        value={selectedPermissionToAdd}
                        onChange={(e) => setSelectedPermissionToAdd(e.target.value)}
                        label="Select Permission"
                        disabled={saving}
                      >
                        {navigationItems
                          .filter(nav => {
                            // Only show permissions that don't already exist
                            const existingPerm = existingPermissions.find(
                              p => (p.tab_name === nav.permission || p.tab_name === nav.name?.toLowerCase()) ||
                                   (p.permission === nav.permission || p.permission === nav.name?.toLowerCase())
                            );
                            return !existingPerm;
                          })
                          .map((nav) => (
                            <MenuItem key={nav.permission} value={nav.permission}>
                              {nav.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddSinglePermission}
                      disabled={!selectedPermissionToAdd || saving}
                      startIcon={<Add />}
                    >
                      Add
                    </Button>
                  </Box>
                </Box> */}

                {/* Permissions List */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Existing Permissions
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflowY: "auto",
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <List dense>
                      {navigationItems.map((nav) => {
                        const existingPerm = existingPermissions.find(
                          p => (p.tab_name === nav.permission || p.tab_name === nav.name?.toLowerCase()) ||
                               (p.permission === nav.permission || p.permission === nav.name?.toLowerCase())
                        );
                        const permissionId = existingPerm?.id || existingPerm?.permission_id;
                        const currentAllowed = existingPerm?.allowed === true || existingPerm?.allowed === "true";
                        
                        return (
                          <ListItem
                            key={nav.permission}
                            sx={{
                              px: 0,
                              py: 1,
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                              "&:last-child": {
                                borderBottom: "none",
                              },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              backgroundColor: permissionId ? alpha(theme.palette.success.main, 0.05) : "transparent",
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: permissionId ? 500 : 400 }}>
                              {nav.name}
                              {permissionId && (
                                <Chip 
                                  label={currentAllowed ? "Allowed" : "Denied"} 
                                  size="small" 
                                  color={currentAllowed ? "success" : "default"}
                                  sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                              {/* Yes/No Checkboxes - Always visible */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={navigationPermissions[nav.permission] === true}
                                    onChange={() => handleNavigationPermissionChange(nav.permission, true)}
                                    size="small"
                                  />
                                }
                                label="Yes"
                                sx={{ m: 0 }}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={navigationPermissions[nav.permission] === false}
                                    onChange={() => handleNavigationPermissionChange(nav.permission, false)}
                                    size="small"
                                  />
                                }
                                label="No"
                                sx={{ m: 0 }}
                              />
                              
                              {/* Action buttons - only show for existing permissions */}
                              {/* {permissionId && (
                                <>
                                  <Tooltip title="Delete Permission">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => handleDeletePermission(permissionId)}
                                      sx={{
                                        ml: 1,
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                                        }
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )} */}
                            </Box>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePermissionDialog} disabled={saving}>
            Close
          </Button>
          <Button
            onClick={handleSavePermissions}
            variant="outlined"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            {saving ? "Saving..." : "Add/ Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Permission;

